import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getContractorDetails, updateContractor, clearContractorsError, clearContractorsSuccess } from '../../../../redux/Slices/contractorsSlice';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { applyFieldFilter } from '../../../../shared/utils.js/fieldValidation';

const EditContractor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { details: contractorDetails, isLoading, error, success } = useSelector((state) => state.contractors);
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    tax_number: '',
    address: '',
    phone: '',
    email: '',
    is_active: true
  });
  const lastErrorRef = useRef({ message: null, time: 0 });

  useEffect(() => {
    if (id) {
      dispatch(getContractorDetails(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (contractorDetails) {
      setFormData({
        name_ar: contractorDetails.name_ar || '',
        name_en: contractorDetails.name_en || '',
        tax_number: contractorDetails.tax_number || '',
        address: contractorDetails.address || '',
        phone: contractorDetails.phone || '',
        email: contractorDetails.email || '',
        is_active: contractorDetails.is_active !== undefined ? contractorDetails.is_active : true
      });
    }
  }, [contractorDetails]);

  useEffect(() => {
    if (success) {
      toast.success(success, { 
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      setTimeout(() => {
        navigate('/contractors');
        dispatch(clearContractorsSuccess());
      }, 2000);
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { 
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearContractorsError()), 3000);
    }
  }, [success, error, navigate, dispatch, isRTL]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : applyFieldFilter(name, value);
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const updatedData = {};
    if (formData.name_ar !== contractorDetails?.name_ar) {
      updatedData.name_ar = formData.name_ar;
    }
    if (formData.name_en !== contractorDetails?.name_en) {
      updatedData.name_en = formData.name_en;
    }
    if (formData.tax_number !== contractorDetails?.tax_number) {
      updatedData.tax_number = formData.tax_number;
    }
    if (formData.address !== contractorDetails?.address) {
      updatedData.address = formData.address;
    }
    if (formData.phone !== contractorDetails?.phone) {
      updatedData.phone = formData.phone;
    }
    if (formData.email !== contractorDetails?.email) {
      updatedData.email = formData.email;
    }
    if (formData.is_active !== contractorDetails?.is_active) {
      updatedData.is_active = formData.is_active;
    }
    
    if (Object.keys(updatedData).length === 0) {
      toast.info(t('contractors.editForm.noChanges'), { rtl: isRTL });
      return;
    }
    
    await dispatch(updateContractor({ id, contractorData: updatedData }));
  };

  const handleCancel = () => {
    navigate('/contractors');
  };

  if (isLoading && !contractorDetails) {
    return (
      <div style={{ 
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        direction: dir,
        textAlign
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('contractors.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir,
      textAlign
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: '#1b3350',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid #333'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #333',
            backgroundColor: '#1a1f2e'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 style={{ 
                margin: 0, 
                fontSize: '24px', 
                fontWeight: 'bold',
                color: 'white',
                textAlign
              }}>
                {t('contractors.editForm.title')}
              </h1>
            
              <button
                onClick={handleCancel}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse'
                }}
              >
                <FaArrowLeft />
                {t('contractors.editForm.back')}
              </button>
            </div>
          </div>

          <div style={{ padding: '30px' }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                {/* الاسم بالعربية */}
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '14px',
                    textAlign
                  }}>
                    {t('contractors.editForm.nameAr')}
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={formData.name_ar}
                    onChange={handleChange}
                    placeholder={t('contractors.editForm.nameArPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign
                    }}
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '14px',
                    textAlign
                  }}>
                    {t('contractors.editForm.nameEn')}
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={formData.name_en}
                    onChange={handleChange}
                    placeholder={t('contractors.editForm.nameEnPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign
                    }}
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '14px',
                    textAlign
                  }}>
                    {t('contractors.editForm.taxNumber')}
                  </label>
                  <input
                    type="text"
                    name="tax_number"
                    value={formData.tax_number}
                    onChange={handleChange}
                    placeholder={t('contractors.editForm.taxNumberPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign
                    }}
                  />
                </div>

                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '14px',
                    textAlign
                  }}>
                    {t('contractors.editForm.address')}
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder={t('contractors.editForm.addressPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign
                    }}
                  />
                </div>

                {/* الهاتف */}
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '14px',
                    textAlign
                  }}>
                    {t('contractors.editForm.phone')}
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('contractors.editForm.phonePlaceholder')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign
                    }}
                  />
                </div>

                {/* البريد الإلكتروني */}
                <div>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: 'white',
                    fontSize: '14px',
                    textAlign
                  }}>
                    {t('contractors.editForm.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contractors.editForm.emailPlaceholder')}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: dir,
                      textAlign
                    }}
                  />
                </div>

                {/* الحالة */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <label style={{ 
                    color: 'white',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign
                  }}>
                    {t('contractors.editForm.isActive')}
                  </label>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '15px',
                marginTop: '30px'
              }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  {t('contractors.editForm.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    backgroundColor: '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: isLoading ? 0.7 : 1,
                    flexDirection: isRTL ? 'row' : 'row-reverse'
                  }}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      {t('contractors.editForm.saving')}
                    </>
                  ) : (
                    <>
                      <FaSave />
                      {t('contractors.editForm.save')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditContractor;

